import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, tap } from 'rxjs';

import type { Maybe, Menu } from '@evc/web-components';
import { breakpoints, ResponsiveService } from '@evc/web-components';

import { NavigationsMenu } from './types';

export type TertiaryNavigation = Record<NavigationsMenu, Menu>;

@Injectable({ providedIn: 'root' })
export class NavigationsService {
  readonly #router = inject(Router);
  readonly #responsiveService = inject(ResponsiveService);

  constructor() {
    this.#autoCloseOnNavigation();
  }

  // ############### MENUS ############### //

  readonly #activeMenu = signal<Maybe<NavigationsMenu>>(undefined);
  /** Some menus may watch this to auto open/close
   * - it is not yet implemented for all (only apps-menu for now)
   */
  readonly activeMenu = this.#activeMenu.asReadonly();

  openMenu(menu: NavigationsMenu): void {
    const currentMenu = this.#activeMenu();
    if (currentMenu === menu) return;

    this.#activeMenu.set(menu);
  }

  /** You must provide a menu name so we ensure to not close another opened in-between
   * - provide `true` to force close whatever menu
   */
  closeMenu(menu: NavigationsMenu | true): void {
    const currentMenu = this.#activeMenu();
    if (menu && currentMenu !== menu) return;

    this.#activeMenu.set(undefined);
  }

  // ############### LEFTBAR ############### //

  /** user action take priority
   * - true if user expanded it
   * - false if user collapsed it
   * - undefined if user has not set a preference yet
   */
  readonly userLeftbarState = signal<Maybe<boolean>>(undefined);
  /** responsive layout drive auto leftbar open
   * - only if user did never click
   */
  readonly responsiveLeftbarState = this.#responsiveService.above(breakpoints.desktop, {
    signal: true,
  });

  readonly isLeftbarOpen = computed(() => this.userLeftbarState() ?? this.responsiveLeftbarState());

  toggleLeftbar(forcedState?: boolean): void {
    const nextOpenState = typeof forcedState === 'boolean' ? forcedState : !this.isLeftbarOpen();

    this.userLeftbarState.set(nextOpenState);
  }

  #removeUserLeftbarStateAfterLayoutChangeEffect = effect(() => {
    this.responsiveLeftbarState();

    untracked(() => {
      setTimeout(() => {
        this.userLeftbarState.set(undefined);
      });
    });
  });

  // ############### BURGER ############### //

  readonly isBurgerMenuOpen = signal(false);
  toggleBurgerMenu(forcedState?: boolean): void {
    const nextOpenState = typeof forcedState === 'boolean' ? forcedState : !this.isBurgerMenuOpen();

    this.isBurgerMenuOpen.set(nextOpenState);
  }

  // ############### OTHER ############### //

  #autoCloseOnNavigation(): void {
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap(() => this.closeMenu(true)),
        tap(() => this.toggleBurgerMenu(false)),
      )
      .subscribe();
  }
}
